import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import formatsSvg from 'assets/icons/formats.svg';
import { FormatLabel } from 'store/userOptions/reducer.types';
import { CodeNameModel } from 'components/common/types';
import { Option } from 'components/common/types/DropdownItem.types';
import { Store } from 'components/common/types/Store.types';
import { BaseSelectMultiLevel } from 'lib/BaseSelect';
import { FormatItem } from 'components/pages/AvailabilityReport/AddFilter/AddFilter.types';
import { sortByName } from 'utils/sortByName';

interface FilterFormatsProps {
  selectedFormats?: FormatItem[];
  changeFilters: (filters: { formats: FormatItem[] }) => void;
  isDisabled?: boolean;
}

const FilterFormats: React.FC<FilterFormatsProps> = ({ selectedFormats = [], changeFilters, isDisabled = false }) => {
  const availableFormats = useSelector(
    (state: Store) => state.userOptions.referenceOptions.format as unknown as Record<string, CodeNameModel[]>,
  );
  const isLoading = useSelector((state: Store) => state.dealManagement.isFetchingReferences);

  type FormatKey = keyof typeof FormatLabel;

  const formatOptions: Option[] = sortByName(
    (Object.entries(availableFormats) as [FormatKey, CodeNameModel[]][]).reduce<Option[]>((acc, [groupKey, items]) => {
      if (items.length > 0) {
        acc.push({
          code: groupKey,
          name: FormatLabel[groupKey],
          selectable: false,
          children: sortByName(
            items.map(({ code, name }) => ({
              code,
              name,
              selectable: true,
              parentCode: groupKey,
              parentName: FormatLabel[groupKey],
            })),
          ),
        });
      }
      return acc;
    }, []),
  );

  const selectedFormatCodes = useMemo(() => {
    return selectedFormats.map((format) => format.code);
  }, [selectedFormats]);

  const handleFormatsChange = (node: Option, checked: boolean): void => {
    const formatItem: FormatItem = {
      code: String(node.code),
      name: node.name,
      parentCode: String(node.parentCode),
      parentName: FormatLabel[node.parentCode as FormatKey] || String(node.parentCode),
    };

    const updatedFormats = checked
      ? [...selectedFormats, formatItem]
      : selectedFormats.filter((format) => format.code !== formatItem.code);

    changeFilters({ formats: updatedFormats });
  };

  return (
    <BaseSelectMultiLevel
      options={formatOptions}
      value={selectedFormatCodes}
      onChangeItem={handleFormatsChange}
      isDisabled={isDisabled || isLoading}
      dataTestId="formats-multi-level-dropdown"
      icon={formatsSvg}
      placeholder="Formats"
      classNameWrapper="w-52"
      selectedLabel="Formats"
    />
  );
};

export default FilterFormats;
